/**
 * @file XeroScopeBanner.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-24
 * @modified 2026-09-24
 * @description Asks one user to reconnect Xero, once their connection is short
 *              of the permissions we now request and the deadline is close.
 *
 *              Xero retires the broad Accounting API scopes on 13 September
 *              2027. A token keeps whatever scopes it was issued with, so a
 *              connection made before we switched goes on working right up to
 *              that date and then stops. Reconnecting is the whole fix, and it
 *              is the user's own two clicks — which is why this is a strip with
 *              the buttons in it rather than a job for somebody else.
 *
 *              Advisory, deliberately: it borrows MaintenanceBanner's slot so
 *              the app slides down rather than being covered, and it blocks
 *              nothing. Contrast XeroRequiredGate, which blocks because nothing
 *              works without a connection at all. Here everything works until
 *              the deadline, so blocking would invent an outage six weeks early.
 *
 *              The server decides whether to show it (see xeroService
 *              .scopeMigration). An office PC with a skewed clock would
 *              otherwise start nagging early or, worse, never.
 */

import React from 'react';
import { formatDateWithOrdinal } from '../utils/dateFormatters';
import './XeroScopeBanner.css';

export default function XeroScopeBanner({ active, retiresOn, onReconnect, onDisconnect }) {
  const open = !!active;
  const deadline = formatDateWithOrdinal(retiresOn);

  return (
    <div className={`xero-scope-slot${open ? ' open' : ''}`} aria-hidden={!open}>
      <div className="xero-scope-slot-inner">
        <div className="xero-scope-strip" role="status" aria-live="polite">
          <span className="material-symbols-rounded">link_off</span>
          <span className="xero-scope-text">
            <strong>Your Xero connection needs renewing.</strong>{' '}
            Xero is changing the permissions it accepts
            {deadline ? ` on ${deadline}` : ' shortly'}. Reconnecting takes a few
            seconds and keeps everything working — nothing is affected until then.
          </span>
          {/* Reconnect is the fix on its own: consenting again replaces the
              stored token. Disconnect is offered because it is the habit people
              have, and because it is the way out if a reconnect misbehaves. */}
          <button
            type="button"
            className="xero-scope-btn primary"
            data-write-action
            onClick={onReconnect}
          >
            Reconnect Xero
          </button>
          <button
            type="button"
            className="xero-scope-btn"
            data-write-action
            onClick={onDisconnect}
            title="Not normally needed — reconnecting on its own is enough"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}
